import React, { useState, useEffect } from 'react';
import { View, Text, TouchableOpacity, ActivityIndicator, ScrollView, Platform, StyleSheet, TextInput, Alert, Modal, KeyboardAvoidingView } from 'react-native';
import api from '../../services/api';
import AdminLayout from '../components/layout/AdminLayout';
import { Ionicons } from '@expo/vector-icons';
import Table from '../components/common/Table';

const ClustersScreen = ({ navigation }) => {
  const [clusters, setClusters] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Modal states
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedCluster, setSelectedCluster] = useState(null);
  const [modalMode, setModalMode] = useState('edit'); // 'edit' or 'toggle'
  const [editName, setEditName] = useState('');
  const [editStatus, setEditStatus] = useState('active');
  const [editReason, setEditReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchClusters();
  }, []);

  const fetchClusters = async () => {
    try {
      setIsLoading(true);
      const response = await api.get('/admin/clusters');
      if (response.data.success) {
        const transformedData = response.data.data.map(cluster => ({
          ...cluster,
          fcpCount: cluster.fcps?.length || 0,
          participantCount: cluster.fcps?.reduce((acc, fcp) => acc + (fcp.participants?.length || 0), 0) || 0
        }));
        setClusters(transformedData);
      } else {
        throw new Error('Failed to fetch clusters');
      }
    } catch (err) {
      setError('Failed to load clusters');
      console.error('Error fetching clusters:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const openEditModal = (cluster) => {
    setSelectedCluster(cluster);
    setEditName(cluster.name);
    setEditStatus(cluster.status);
    setEditReason(cluster.disabledReason || '');
    setModalMode('edit');
    setIsModalVisible(true);
  };

  const openToggleModal = (cluster) => {
    setSelectedCluster(cluster);
    setEditStatus(cluster.status === 'active' ? 'disabled' : 'active');
    setEditReason(cluster.disabledReason || '🚧 Scheduled maintenance in your region.');
    setModalMode('toggle');
    setIsModalVisible(true);
  };

  const handleSubmit = async () => {
    if (!selectedCluster) return;
    
    try {
      setIsSubmitting(true);
      const response = await api.patch(`/admin/clusters/${selectedCluster.id}/status`, {
        status: editStatus,
        disabledReason: editStatus === 'disabled' ? editReason : null
      });

      if (response.data.success) {
        await fetchClusters();
        setIsModalVisible(false);
        if (Platform.OS === 'web') {
          alert(`✨ Cluster ${selectedCluster.name} has been updated successfully!`);
        } else {
          Alert.alert('Success ✨', `Cluster ${selectedCluster.name} has been updated successfully!`);
        }
      }
    } catch (err) {
      console.error('Error updating cluster:', err);
      alert('❌ Failed to update cluster');
    } finally {
      setIsSubmitting(false);
    }
  };

  const columns = [
    { field: 'name', header: '📍 Cluster Name', sortable: true },
    { field: 'fcpCount', header: '🏢 FCPs', sortable: true },
    { field: 'participantCount', header: '👥 Participants', sortable: true },
    {
      field: 'status',
      header: '🛡️ Status',
      sortable: true,
      render: (value) => (
        <View style={[
          styles.badge,
          value === 'active' ? styles.activeBadge : styles.disabledBadge
        ]}>
          <Text style={[
            styles.badgeText,
            value === 'active' ? styles.activeText : styles.disabledText
          ]}>
            {value === 'active' ? '✅ ACTIVE' : '🚫 DISABLED'}
          </Text>
        </View>
      )
    },
    {
      field: 'disabledReason',
      header: '📝 Notice',
      render: (value) => <Text style={styles.reasonText} numberOfLines={1}>{value || '-'}</Text>
    }
  ];

  const handleBack = () => {
    navigation.navigate('AdminPanel');
  };

  return (
    <AdminLayout>
      <View style={styles.container}>
        {/* Header */}
        <View style={styles.header}>
          <TouchableOpacity onPress={handleBack} style={styles.backButton}>
            <Ionicons name="arrow-back" size={20} color="#6B7280" />
            <Text style={styles.backButtonText}>Back</Text>
          </TouchableOpacity>
          <Text style={styles.headerTitle}>🌍 Cluster Management</Text>
          <View style={{ width: 40 }} />
        </View>

        <ScrollView style={styles.mainContent}>
          <View style={styles.contentWrapper}>
            <View style={styles.pageHeader}>
              <View>
                <Text style={styles.pageTitle}>Clusters 🗺️</Text>
                <Text style={styles.pageSubtitle}>Manage geographic clusters and service availability with precision 🎯</Text>
              </View>
            </View>

            <View style={styles.contentCard}>
              {error && (
                <View style={styles.errorContainer}>
                  <Text style={styles.errorText}>⚠️ {error}</Text>
                </View>
              )}

              {isLoading ? (
                <View style={styles.loadingContainer}>
                  <ActivityIndicator size="large" color="#6366F1" />
                  <Text style={styles.loadingText}>Fetching clusters... ⏳</Text>
                </View>
              ) : (
                <View style={styles.tableContainer}>
                  <Table
                    columns={columns}
                    data={clusters}
                    onEdit={(cluster) => openEditModal(cluster)}
                  />
                </View>
              )}
            </View>
          </View>
        </ScrollView>

        {/* Enhancement: Clean Modal for Editing/Toggling */}
        <Modal
          visible={isModalVisible}
          transparent={true}
          animationType="fade"
          onRequestClose={() => setIsModalVisible(false)}
        >
          <View style={styles.modalOverlay}>
            <KeyboardAvoidingView 
              behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
              style={styles.modalContent}
            >
              <View style={styles.modalHeader}>
                <Text style={styles.modalTitle}>
                  {modalMode === 'edit' ? '🛠️ Edit Cluster' : '🛡️ Cluster Status'}
                </Text>
                <TouchableOpacity onPress={() => setIsModalVisible(false)}>
                  <Ionicons name="close" size={24} color="#94A3B8" />
                </TouchableOpacity>
              </View>

              <ScrollView style={styles.modalBody}>
                <Text style={styles.inputLabel}>Cluster Name</Text>
                <View style={[styles.inputContainer, styles.disabledInput]}>
                  <TextInput
                    style={styles.input}
                    value={editName}
                    editable={false}
                  />
                  <Ionicons name="lock-closed" size={16} color="#CBD5E1" />
                </View>

                <Text style={styles.inputLabel}>Current Status</Text>
                <View style={styles.statusToggleContainer}>
                  <TouchableOpacity 
                    style={[
                      styles.statusOption, 
                      editStatus === 'active' && styles.statusOptionActive
                    ]}
                    onPress={() => setEditStatus('active')}
                  >
                    <Ionicons 
                      name="checkmark-circle" 
                      size={20} 
                      color={editStatus === 'active' ? '#FFFFFF' : '#94A3B8'} 
                    />
                    <Text style={[
                      styles.statusOptionText,
                      editStatus === 'active' && styles.statusOptionTextActive
                    ]}>Active</Text>
                  </TouchableOpacity>

                  <TouchableOpacity 
                    style={[
                      styles.statusOption, 
                      editStatus === 'disabled' && styles.statusOptionDisabled
                    ]}
                    onPress={() => setEditStatus('disabled')}
                  >
                    <Ionicons 
                      name="remove-circle" 
                      size={20} 
                      color={editStatus === 'disabled' ? '#FFFFFF' : '#94A3B8'} 
                    />
                    <Text style={[
                      styles.statusOptionText,
                      editStatus === 'disabled' && styles.statusOptionTextActive
                    ]}>Disabled</Text>
                  </TouchableOpacity>
                </View>

                {editStatus === 'disabled' && (
                  <>
                    <Text style={styles.inputLabel}>Suspension Notice (Visible to Users) 📣</Text>
                    <View style={styles.textAreaContainer}>
                      <TextInput
                        style={styles.textArea}
                        value={editReason}
                        onChangeText={setEditReason}
                        placeholder="e.g. 🚧 Scheduled maintenance in your region."
                        multiline={true}
                        numberOfLines={4}
                        textAlignVertical="top"
                      />
                    </View>
                    <Text style={styles.helperText}>
                      This text will be shown on the "Service Unavailable" screen for all users in this cluster.
                    </Text>
                  </>
                )}
              </ScrollView>

              <View style={styles.modalFooter}>
                <TouchableOpacity 
                  style={styles.cancelButton}
                  onPress={() => setIsModalVisible(false)}
                >
                  <Text style={styles.cancelButtonText}>Cancel</Text>
                </TouchableOpacity>
                <TouchableOpacity 
                  style={[styles.saveButton, isSubmitting && styles.saveButtonDisabled]}
                  onPress={handleSubmit}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <ActivityIndicator size="small" color="#FFFFFF" />
                  ) : (
                    <Text style={styles.saveButtonText}>Save Changes ✨</Text>
                  )}
                </TouchableOpacity>
              </View>
            </KeyboardAvoidingView>
          </View>
        </Modal>
      </View>
    </AdminLayout>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F8FAFC'
  },
  header: {
    backgroundColor: '#FFFFFF',
    padding: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#E2E8F0',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  backButton: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  backButtonText: {
    marginLeft: 8,
    color: '#64748B',
    fontSize: 14,
    fontWeight: '500',
  },
  headerTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: '#1E293B',
  },
  mainContent: {
    flex: 1
  },
  contentWrapper: {
    padding: 20,
  },
  pageHeader: {
    marginBottom: 24,
  },
  pageTitle: {
    fontSize: 28,
    fontWeight: '800',
    color: '#0F172A',
    letterSpacing: -0.5,
  },
  pageSubtitle: {
    marginTop: 6,
    fontSize: 15,
    color: '#64748B',
    lineHeight: 22,
  },
  contentCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    shadowColor: '#6366F1',
    shadowOffset: { width: 0, height: 10 },
    shadowOpacity: 0.05,
    shadowRadius: 20,
    elevation: 3,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: '#F1F5F9',
  },
  tableContainer: {
    padding: 0
  },
  badge: {
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 8,
    alignSelf: 'flex-start',
  },
  badgeText: {
    fontSize: 10,
    fontWeight: '800',
    letterSpacing: 0.5,
  },
  activeBadge: {
    backgroundColor: '#DCFCE7',
  },
  activeText: {
    color: '#166534',
  },
  disabledBadge: {
    backgroundColor: '#FEE2E2',
  },
  disabledText: {
    color: '#991B1B',
  },
  reasonText: {
    fontSize: 12,
    color: '#94A3B8',
    fontStyle: 'italic'
  },
  loadingContainer: {
    padding: 60,
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 16,
    color: '#64748B',
    fontSize: 14,
    fontWeight: '500',
  },
  errorContainer: {
    margin: 20,
    padding: 16,
    backgroundColor: '#FEF2F2',
    borderRadius: 12,
    borderLeftWidth: 4,
    borderLeftColor: '#EF4444',
  },
  errorText: {
    color: '#991B1B',
    fontWeight: '500',
  },
  
  // Modal Styles
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(15, 23, 42, 0.6)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContent: {
    backgroundColor: '#FFFFFF',
    borderRadius: 24,
    width: '100%',
    maxWidth: 500,
    maxHeight: '90%',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 20 },
    shadowOpacity: 0.25,
    shadowRadius: 30,
    elevation: 10,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 24,
    borderBottomWidth: 1,
    borderBottomColor: '#F1F5F9',
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: '800',
    color: '#0F172A',
    letterSpacing: -0.5,
  },
  modalBody: {
    padding: 24,
  },
  inputLabel: {
    fontSize: 14,
    fontWeight: '700',
    color: '#475569',
    marginBottom: 8,
    marginTop: 16,
  },
  inputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#F8FAFC',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 12,
    paddingHorizontal: 16,
    height: 48,
  },
  disabledInput: {
    backgroundColor: '#F1F5F9',
    opacity: 0.8,
  },
  input: {
    flex: 1,
    fontSize: 15,
    color: '#1E293B',
    fontWeight: '500',
  },
  statusToggleContainer: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 4,
  },
  statusOption: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    backgroundColor: '#FFFFFF',
    gap: 8,
  },
  statusOptionActive: {
    backgroundColor: '#10B981',
    borderColor: '#10B981',
  },
  statusOptionDisabled: {
    backgroundColor: '#EF4444',
    borderColor: '#EF4444',
  },
  statusOptionText: {
    fontSize: 14,
    fontWeight: '700',
    color: '#64748B',
  },
  statusOptionTextActive: {
    color: '#FFFFFF',
  },
  textAreaContainer: {
    backgroundColor: '#F8FAFC',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 12,
    padding: 12,
    minHeight: 100,
  },
  textArea: {
    fontSize: 15,
    color: '#1E293B',
    fontWeight: '500',
    lineHeight: 22,
  },
  helperText: {
    fontSize: 12,
    color: '#94A3B8',
    marginTop: 8,
    fontStyle: 'italic',
  },
  modalFooter: {
    flexDirection: 'row',
    padding: 24,
    borderTopWidth: 1,
    borderTopColor: '#F1F5F9',
    gap: 12,
  },
  cancelButton: {
    flex: 1,
    paddingVertical: 12,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#E2E8F0',
  },
  cancelButtonText: {
    fontSize: 15,
    fontWeight: '700',
    color: '#64748B',
  },
  saveButton: {
    flex: 2,
    backgroundColor: '#6366F1',
    paddingVertical: 12,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 12,
    shadowColor: '#6366F1',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.2,
    shadowRadius: 8,
  },
  saveButtonDisabled: {
    opacity: 0.7,
  },
  saveButtonText: {
    fontSize: 15,
    fontWeight: '700',
    color: '#FFFFFF',
  },
});

export default ClustersScreen;
